import { Drawer } from "@mui/material";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import SmallSearchBar from "components/shared/SmallSearchBar";
import TextButton from "components/shared/TextButton";
import TitleCover from "components/shared/TitleCover";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { salesStatusRules } from "containers/TransferTerms";
import TransferTermDrawer from "containers/TransferTerms/TransferTermDrawer";
import HandleRightsModal from "containers/TransferTerms/TransferTermSalesTable/HandleRightsModal";
import PaymentDateModal from "containers/TransferTerms/TransferTermSalesTable/PaymentDateModal";
import SaveInvoiceModal from "containers/TransferTerms/TransferTermSalesTable/SaveInvoiceModal";
import { formatLocaleDateString } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTransfereeTerms } from "store/transfereesStore";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";
import type { TFetchStatus } from "store/types";

import styles from "./style.module.scss";

interface ITransfereeTermsTable {
    transfereeId: number | undefined;
    isLoading?: boolean;
}

/**
 * Table on Transferee page displaying list of terms linked to this transferee.
 */
const TransfereeTermsTable = ({ transfereeId, isLoading = false }: ITransfereeTermsTable) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const { hasBexioIntegration } = useAppSelector((state) => state.integrations);

    const [chosenTerm, setChosenTerm] = useState<ITransferTerm>();
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    const { transfereeTerms } = useAppSelector((state) => state.transferees);
    const [termsStatus, setTermsStatus] = useState<TFetchStatus>("pending");
    const [currentSales, setCurrentSales] = useState<ITransferTermSale[]>([]);
    const [currentSale, setCurrentSale] = useState<ITransferTermSale>();
    const [formMode, setFormMode] = useState<string>("");
    const [modalType, setModalType] = useState<string>("");

    const {
        query,
        handleQuery,
        count,
        page,
        limitPagination,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        requestStatus,
        setRequestStatus,
    } = useAPIPagination({ type: "transfereeTerms", itemId: transfereeId });

    const APIData = { count, page, limitPagination, handleChangePage, handleChangeLimit, handleSort, setRequestStatus };

    const handleToggleDrawer = async (value: boolean, term?: ITransferTerm | number) => {
        if (value) {
            await setChosenTerm(term as ITransferTerm);
            setDrawerOpen(true);
        } else {
            setChosenTerm(undefined);
            setDrawerOpen(false);
        }
    };

    /**
     * Opens modal for sale actions.
     * @param event Action event.
     * @param currentSale Sale from which the action is triggered.
     * @param modalType Type of modal to open.
     * @param mode (new or edit) Needed if there are two states for the modal (mainly addRights)
     */
    const handleOpeningModal = async (
        currentSale: ITransferTermSale | undefined,
        modalType: string,
        mode?: string,
        event?: any,
    ) => {
        if (event) event.stopPropagation();
        await setCurrentSale(currentSale);
        if (mode) {
            await setFormMode(mode);
        }
        setModalType(modalType);
    };

    const handleCloseModal = ({
        updatedSale,
        updatedSales,
    }: {
        updatedSale?: ITransferTermSale;
        updatedSales?: ITransferTermSale[];
    }) => {
        setModalType("");
        updatedSale ? setCurrentSale(updatedSale) : setCurrentSale(undefined);
        updatedSales ? setCurrentSales(updatedSales) : setCurrentSales([]);
    };

    const formatTitleColumn = (term: ITransferTerm) => {
        const workLabel = transferWorkLabel(term);
        return (
            <div className={styles["title-name-image"]}>
                <TitleCover
                    title={workLabel}
                    coverUrl={term.workAvatarUrl ?? term.titleAvatarUrl ?? undefined}
                    style={{ minWidth: "40px", maxWidth: "40px" }}
                    id={(term.workId ?? term.titleId)?.toString() ?? ""}
                    color="work-color"
                />
                <div className={styles["title-name"]}>
                    <span className={styles["name"]}>{workLabel}</span>

                    {/** We shouldn't have deleted terms appearing, but just in case: */}
                    {term.isDeleted ? (
                        <span className={styles["date"]}>{t("TransferTerms.TermTable.TermDeleted")}</span>
                    ) : (
                        <span className={styles["date"]}>
                            {t("Transferees.CreatedAt", {
                                date: formatLocaleDateString({
                                    date: term.createdAt,
                                    options: { numeric: true, timeZone: "UTC" },
                                }),
                            })}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const termsColumns: LastElementTableColumns[] = [
        {
            id: "titleName",
            label: "Contrat",
            format: (value, id, currentRow) => formatTitleColumn(currentRow),
            align: "left",
        },
        {
            id: "startDate",
            label: t("Transferees.StartDate"),
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "expirationDate",
            label: t("Transferees.EndDate"),
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "details",
            label: "",
            align: "right",
            format: (value, id, currentRow) => (
                <TextButton
                    text={t("Transferees.SeeDetail")}
                    handleClick={() => handleToggleDrawer(true, currentRow)}
                    style={{
                        color: "#4C93E6",
                        fontSize: "11px",
                        textDecoration: "underline",
                    }}
                    colorOnHover="#275ab0"
                />
            ),
        },
    ];

    const transfereeChanged = useMemo(() => {
        return transfereeTerms[0]?.transfereeId !== transfereeId;
    }, [transfereeTerms, transfereeId]);

    useEffect(() => {
        if (transfereeId && transfereeChanged) {
            setTermsStatus("pending");
            dispatch(fetchTransfereeTerms({ transfereeId, options: { limit: 25, sortOrder: "DESC" } })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setTermsStatus("succeeded");
                } else if (resp.meta.requestStatus === "rejected") {
                    setTermsStatus("failed");
                }
            });
        }
    }, [transfereeId]);

    return (
        <div className={styles["transferee-terms-table"]}>
            <SmallSearchBar
                filter={query}
                onSearch={handleQuery}
                placeholder={t("Transferees.SearchWork")}
                style={{ width: "100%" }}
            />

            <LastElementTable
                data={transfereeTerms}
                dataType={"transfereeTerms"}
                columns={termsColumns}
                APIData={APIData}
                max={-1}
                isLoading={
                    isLoading ||
                    (transfereeChanged ? ["pending", "idle"].includes(termsStatus) : requestStatus === "pending")
                }
                columnsNumber={3}
            />
            <Drawer open={drawerOpen} onClose={() => handleToggleDrawer(false)} anchor="right">
                <TransferTermDrawer
                    transferTerm={chosenTerm}
                    handleToggleDrawer={handleToggleDrawer}
                    salesStatusRules={salesStatusRules()}
                    currentSales={currentSales}
                    setCurrentSales={setCurrentSales}
                    handleOpeningModal={handleOpeningModal}
                />

                {/** MODALS FOR SALES UPDATE -- those shared between payments table and term drawer */}
                <ModalBase
                    modalTitle={
                        formMode === "new"
                            ? t("TransferTerms.Modals.EnterRoyalties")
                            : t("TransferTerms.Modals.UpdateRoyalties")
                    }
                    modalSubtitle={t("TransferTerms.Modals.EnterRoyaltiesSubtitle")}
                    open={modalType === "addRights"}
                    onClose={() => setModalType("")}
                >
                    <HandleRightsModal
                        termSale={currentSale}
                        handleClose={handleCloseModal}
                        formMode={formMode}
                        currentSales={currentSales}
                    />
                </ModalBase>
                <ModalBase
                    modalTitle={
                        formMode === "new"
                            ? t("TransferTerms.Modals.SendInvoiceQuestion")
                            : t("TransferTerms.Modals.SaveInvoice")
                    }
                    modalSubtitle={t("TransferTerms.Modals.SaveInvoiceSubtitle")}
                    open={modalType === "addInvoice"}
                    onClose={() => setModalType("")}
                >
                    <SaveInvoiceModal
                        termSale={currentSale}
                        handleClose={handleCloseModal}
                        formMode="new"
                        currentSales={currentSales}
                        hasBexio={hasBexioIntegration}
                    />
                </ModalBase>
                <ModalBase
                    modalTitle={
                        formMode === "new"
                            ? t("TransferTerms.Modals.PayQuestion")
                            : t("TransferTerms.Modals.UpdatePayment")
                    }
                    modalSubtitle={t("TransferTerms.Modals.PayModalSubtitle")}
                    open={modalType === "payStatus"}
                    onClose={() => setModalType("")}
                >
                    <PaymentDateModal
                        termSale={currentSale}
                        handleClose={handleCloseModal}
                        formMode={formMode}
                        currentSales={currentSales}
                    />
                </ModalBase>
            </Drawer>
        </div>
    );
};

export default TransfereeTermsTable;
